/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';

import { breakpoints } from '@coursera/cds-common';
import { Button, InlineNotification, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import PasswordConfirmationModal from 'bundles/account-settings/components/sections/session-invalidation/PasswordConfirmationModal';
import * as actions from 'bundles/authentication-common/components/actions';

import _t from 'i18n!nls/account-settings';

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
  `,
  header: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-100);
    align-items: center;
    justify-content: space-between;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      align-items: stretch;
    }
  `,
  text: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
  heading: css`
    && {
      margin: 0;
    }
  `,
  button: css`
    white-space: nowrap;
    margin-top: var(--cds-spacing-150);

    ${breakpoints.down('sm')} {
      white-space: normal;
      width: 100%;
      max-width: unset;
      margin-top: 0;
    }
  `,
  notification: css`
    margin-top: var(--cds-spacing-150);
  `,
};

const SessionInvalidation = () => {
  const [success, setSuccess] = useState(false);
  const [modal, setModal] = useState(false);
  const track = useTracker();

  const close = useCallback(() => {
    setModal(false);
  }, [setModal]);

  const open = useCallback(() => {
    track('click_button', {
      button: { name: 'account_setting_invalidate_session_btn' },
    });
    setModal(true);
  }, [setModal, track]);

  const invalidate = useCallback(
    async (password: string) => {
      setSuccess(false);
      await actions.invalidate({ password });
      setSuccess(true);
      close();
    },
    [close]
  );

  return (
    <section css={styles.root}>
      {modal && (
        <PasswordConfirmationModal
          description={_t(
            'Enter your Coursera password to confirm you want to log out of your account on other devices.'
          )}
          isOpen={modal}
          onCancel={close}
          onConfirm={invalidate}
          submit={_t('Confirm logout')}
          title={_t('Enter your password')}
        />
      )}

      <div css={styles.header}>
        <div css={styles.text}>
          <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
            {_t('Connected devices')}
          </Typography2>
          <Typography2 variant="bodySecondary" component="p" color="supportText">
            {_t('If your account has been logged into on multiple devices, you can log out from here.')}
          </Typography2>
        </div>
        <Button variant="secondary" size="small" onClick={open} css={styles.button}>
          {_t('Log out from all devices')}
        </Button>
      </div>

      <div role="status" aria-live="polite">
        {success && (
          <InlineNotification severity="success" css={styles.notification}>
            {_t('You successfully logged out from the rest of your devices.')}
          </InlineNotification>
        )}
      </div>

      <div role="alert" aria-live="assertive" />
    </section>
  );
};

export default SessionInvalidation;
